import React, { useEffect, useMemo, useState } from 'react';
import { BackupOutlined, Search } from '@mui/icons-material';
import { Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, InputBase, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const ProductBackup = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    api.getProductLevel3Categories()
      .then((response) => { if (active) setCategories(response?.data?.data || []); })
      .catch(() => { if (active) setError('Level 3 categories could not be loaded.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const filteredCategories = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return categories;
    return categories.filter((category) => [category.name, category.categoryName, category.subcategoryName]
      .some((value) => value?.toLowerCase().includes(term)));
  }, [categories, search]);

  const toggleCategory = (categoryId) => {
    setSelectedIds((current) => current.includes(categoryId)
      ? current.filter((id) => id !== categoryId)
      : [...current, categoryId]);
  };

  const selectedCategories = categories.filter((category) => selectedIds.includes(category.productCategoryId));

  const closePasswordDialog = () => {
    if (!downloading) {
      setPasswordOpen(false);
      setPassword('');
    }
  };

  const handleCreateBackup = async (event) => {
    event.preventDefault();
    if (!password.trim() || !selectedCategories.length) return;
    setDownloading(true);
    try {
      const response = await api.createProductBackups(password, selectedCategories.map((category) => ({
        categoryIds: category.productCategoryId,
        name: category.name,
        categoryName: category.categoryName,
        subcategoryName: category.subcategoryName,
      })));
      toast.success(response?.data?.data?.message || 'Category backups created successfully.');
      setPasswordOpen(false);
      setPassword('');
      navigate('/backup');
    } catch (requestError) {
      toast.error(requestError?.response?.data?.message || 'Could not create the product backup.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Button variant="text" onClick={() => navigate('/products/categories')} sx={{ minWidth: 0, p: 0, textTransform: 'none', color: '#1473E6', fontSize: 15 }}>Products</Button>
        <Typography sx={{ color: '#98A2B3' }}>/</Typography>
        <Typography sx={{ color: '#344054', fontSize: 15 }}>Create Product Backup</Typography>
      </Box>
      <Typography sx={{ color: '#1D1F2C', fontSize: { xs: 28, md: 32 }, fontWeight: 700, lineHeight: 1.25, mb: 0.75 }}>Product Backup</Typography>
      <Typography sx={{ color: '#667085', fontSize: 16, mb: 3 }}>Select one or more Level 3 categories to download all of their current products in one backup file.</Typography>

      <Paper variant="outlined" sx={{ borderColor: '#E0E2E7', borderRadius: 2, overflow: 'hidden', boxShadow: '0 3px 10px rgba(16, 24, 40, 0.04)' }}>
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ color: '#1D1F2C', fontSize: 16, fontWeight: 700 }}>Choose categories for backup</Typography>
            <Chip label={`${selectedIds.length} selected`} size="small" sx={{ color: '#512DA8', backgroundColor: '#F3EEFF', fontWeight: 700 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #D0D5DD', borderRadius: 1.5, px: 1.5, width: { xs: '100%', sm: 330 }, backgroundColor: '#fff' }}>
              <Search sx={{ color: '#667085', mr: 1, fontSize: 21 }} />
              <InputBase value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search Level 3 categories" sx={{ flex: 1, py: 0.8, fontSize: 14 }} />
            </Box>
            <Button variant="contained" disabled={!selectedIds.length} startIcon={<BackupOutlined />} onClick={() => setPasswordOpen(true)} sx={{ textTransform: 'none', backgroundColor: '#512DA8', '&:hover': { backgroundColor: '#432584' } }}>Create backup</Button>
          </Box>
        </Box>
        <TableContainer sx={{ borderTop: '1px solid #EAECF0' }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead><TableRow sx={{ backgroundColor: '#F9FAFB' }}>
              <TableCell padding="checkbox" sx={{ pl: 2 }}><Checkbox checked={categories.length > 0 && selectedIds.length === categories.length} indeterminate={selectedIds.length > 0 && selectedIds.length < categories.length} onChange={() => setSelectedIds(selectedIds.length === categories.length ? [] : categories.map((category) => category.productCategoryId))} /></TableCell>
              <TableCell sx={{ width: '28%', color: '#475467', fontWeight: 700, fontSize: 14 }}>Subcategory Level 3</TableCell>
              <TableCell sx={{ width: '22%', color: '#475467', fontWeight: 700, fontSize: 14 }}>Category</TableCell>
              <TableCell sx={{ width: '23%', color: '#475467', fontWeight: 700, fontSize: 14 }}>Subcategory</TableCell>
              <TableCell sx={{ width: '15%', color: '#475467', fontWeight: 700, fontSize: 14 }}>Products to backup</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {filteredCategories.map((category) => <TableRow key={category._id} hover onClick={() => toggleCategory(category.productCategoryId)} sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 }, '&:hover': { backgroundColor: '#FCFAFF' } }}>
                <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}><Checkbox checked={selectedIds.includes(category.productCategoryId)} onChange={() => toggleCategory(category.productCategoryId)} /></TableCell>
                <TableCell sx={{ py: 2.1, color: '#1D2939', fontWeight: 700, fontSize: 15 }}>{category.name}</TableCell>
                <TableCell sx={{ py: 2.1, color: '#475467', fontSize: 14 }}>{category.categoryName || '-'}</TableCell>
                <TableCell sx={{ py: 2.1, color: '#475467', fontSize: 14 }}>{category.subcategoryName || '-'}</TableCell>
                <TableCell sx={{ py: 2.1 }}><Chip label={`${category.productCount || 0} products`} size="small" sx={{ minWidth: 92, color: category.productCount ? '#512DA8' : '#667085', backgroundColor: category.productCount ? '#F3EEFF' : '#F2F4F7', fontWeight: 700 }} /></TableCell>
              </TableRow>)}
              {!filteredCategories.length && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: '#667085' }}>No Level 3 categories found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={passwordOpen} onClose={closePasswordDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box component="form" onSubmit={handleCreateBackup}>
          <Box sx={{ px: 4, pt: 3.5, pb: 2, background: 'linear-gradient(135deg, #FAF8FF 0%, #FFFFFF 72%)', borderBottom: '1px solid #EEE9FA' }}>
            <Typography sx={{ color: '#1D1F2C', fontWeight: 700, fontSize: 23 }}>Create secure backup</Typography>
            <Typography sx={{ color: '#667085', fontSize: 15, lineHeight: 1.55, mt: 0.75 }}>Confirm your password to save the selected category products.</Typography>
          </Box>
          <DialogContent sx={{ px: 4, pt: '24px !important', pb: 1.5 }}>
            <Typography component="label" htmlFor="backup-admin-password" sx={{ display: 'block', color: '#344054', fontWeight: 600, fontSize: 14, mb: 1 }}>Admin password</Typography>
            <TextField
              id="backup-admin-password"
              fullWidth
              autoFocus
              required
              type="password"
              placeholder="Enter your admin password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, minHeight: 54, backgroundColor: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 4, pt: 2.5, pb: 3.5, gap: 1 }}>
            <Button onClick={closePasswordDialog} disabled={downloading} sx={{ minWidth: 92, textTransform: 'none', color: '#475467', fontWeight: 600 }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={downloading || !password.trim()} startIcon={<BackupOutlined />} sx={{ minWidth: 164, py: 1.1, textTransform: 'none', fontWeight: 700, backgroundColor: '#512DA8', '&:hover': { backgroundColor: '#432584' } }}>{downloading ? 'Creating backup…' : 'Create backup'}</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ProductBackup;
